import {
	CONNECTION_STATE_CHANGE,
	ConnectionState,
	PubSub,
} from "@aws-amplify/pubsub";
import { PubSub as PubSubMQTT } from "@aws-amplify/pubsub/mqtt";
import { Hub } from "aws-amplify/utils";
import {
	onUnmounted,
	reactive,
	shallowRef,
	watchEffect,
	type ComputedRef,
	type Ref,
} from "vue";

let pubsub: PubSub = new PubSub();

const activeSubscriptions = new Set<() => void>();
const connectionState = shallowRef<ConnectionState>(
	ConnectionState.Disconnected,
);

export function initLocalMqtt(ip: string, port: string) {
	pubsub = new PubSubMQTT({
		endpoint: `ws://${ip}:${port}/mqtt`,
		aws_appsync_dangerously_connect_to_http_endpoint_for_testing: true,
	}) as PubSub;
}

export function initCloudMqtt(region: string, host: string) {
	pubsub = new PubSub({
		region,
		endpoint: `wss://${host}/mqtt`,
	});
}

export function forceMqttReconnect(region?: string, host?: string) {
	console.log("Forcing MQTT reconnection...");

	if (region && host) {
		pubsub = new PubSub({
			region,
			endpoint: `wss://${host}/mqtt`,
		});
	}

	// Trigger all active subscriptions to reconnect
	activeSubscriptions.forEach(resubscribe => resubscribe());
}

export function forceLocalMqttReconnect(ip: string, port: string) {
	pubsub = new PubSubMQTT({
		endpoint: `ws://${ip}:${port}/mqtt`,
		aws_appsync_dangerously_connect_to_http_endpoint_for_testing: true,
	}) as PubSub;

	// Trigger all active subscriptions to reconnect
	activeSubscriptions.forEach(resubscribe => resubscribe());
}

export function getMqttConnectionState() {
	return connectionState;
}

export function useMqttTopic<T>(
	topic: string | Ref<string | undefined> | ComputedRef<string | undefined>,
) {
	const value = shallowRef<T>();
	let unsubscribe: (() => void) | undefined;
	let currentTopic: string | undefined;

	const subscribe = (topic: string) => {
		currentTopic = topic;
		const subscription = pubsub
			.subscribe({ topics: topic })
			.subscribe(message => {
				value.value = message as unknown as T;
			});
		unsubscribe = () => subscription.unsubscribe();
	};

	const resubscribe = () => {
		if (currentTopic) {
			unsubscribe?.();
			subscribe(currentTopic);
		}
	};

	activeSubscriptions.add(resubscribe);

	if (typeof topic === "string") {
		subscribe(topic);
	} else {
		// is a Ref<string>
		watchEffect(() => {
			unsubscribe?.();
			value.value = undefined;
			if (!topic.value) return;
			subscribe(topic.value);
		});
	}

	onUnmounted(() => {
		unsubscribe?.();
		activeSubscriptions.delete(resubscribe);
	});

	return value;
}
export function getTopic(message: unknown) {
	if (!message) return;
	const symbol = Object.getOwnPropertySymbols(message)[0];
	if (!symbol) return;
	const topic = (message as Record<typeof symbol, string>)[symbol] as string;
	return topic;
}

export function useMqttTopicWildcard<T>(
	topic: string | Ref<string | undefined> | ComputedRef<string | undefined>,
) {
	const value = shallowRef<{ value: T; topic: string }>();
	let unsubscribe: (() => void) | undefined;

	const subscribe = (topic: string) => {
		const subscription = pubsub
			.subscribe({ topics: topic })
			.subscribe(message => {
				const topic = getTopic(message);
				if (!topic) return;
				value.value = {
					value: message as unknown as T,
					topic,
				};
			});
		unsubscribe = () => subscription.unsubscribe();
	};

	if (typeof topic === "string") {
		subscribe(topic);
	} else {
		// is a Ref<string>
		watchEffect(() => {
			unsubscribe?.();
			value.value = undefined;
			if (!topic.value) return;
			subscribe(topic.value);
		});
	}
	onUnmounted(() => unsubscribe?.());
	return value;
}

export function useMqttTopics<T>(
	topics: string[] | Ref<string[]>,
): Map<string, T | undefined> {
	const values = reactive<Map<string, T | undefined>>(new Map());
	let unsubscribe: (() => void) | undefined;

	const subscribe = (topics: string[]) => {
		const subscription = pubsub.subscribe({ topics }).subscribe(message => {
			const value = message as T & { topicSymbol: string };
			(values as Map<string, T>).set(value.topicSymbol, value);
		});
		unsubscribe = () => subscription.unsubscribe();
	};

	if (Array.isArray(topics)) {
		subscribe(topics);
	} else {
		// is Ref<string[]>
		watchEffect(() => {
			unsubscribe?.();
			values.clear();
			subscribe(topics.value);
		});
	}

	onUnmounted(() => unsubscribe?.());

	return values as Map<string, T | undefined>;
}

export async function publish<T>(topic: string, value: T) {
	await pubsub.publish({
		topics: topic,
		message: value as Record<string, unknown>,
	});
}

export function useMqttConnectionStatus() {
	const isConnectedToMachine = shallowRef(false);

	const stop = Hub.listen(
		"pubsub",
		(data: {
			payload: {
				event: string;
				data: { connectionState: ConnectionState };
			};
		}) => {
			const { payload } = data;
			if (payload.event === CONNECTION_STATE_CHANGE) {
				const connectionState = payload.data
					.connectionState as ConnectionState;
				isConnectedToMachine.value =
					connectionState === ConnectionState.Connected;
			}
		},
	);

	onUnmounted(() => stop());

	return isConnectedToMachine;
}
